import {MouseEvent, useCallback} from 'react';
import {createRouteState, useHistory} from './useHistory';

interface HandleNavigationWithSpecialKeysOptions {
  to?: string;
  considerScrollTopAnchor?: boolean;
}

/**
 * Hook that generates a function to be used on Link elements.
 * The generated function changes its behavior based on the event:
 *
 * Ctrl + Click or Meta + Click or Middle mouse button click: Opens link in a new tab.
 * Shift + Click: Opens link in a new window.
 * Normal Click: Navigates to the link in the current tab.
 *
 * Note: The actual behavior of opening links in new tabs or windows is browser-dependent and may
 * also depend on the user's browser settings.
 */
export const useHandleNavigationWithSpecialKeys = ({
  to,
  considerScrollTopAnchor,
}: HandleNavigationWithSpecialKeysOptions) => {
  const history = useHistory();
  const state = createRouteState({
    considerScrollTopAnchor,
  });

  return useCallback(
    (event: MouseEvent<Element>) => {
      if (!to) return;

      event.preventDefault();

      if (event.ctrlKey || event.metaKey || event.button === 1) {
        window.open(to, '_blank');
      } else if (event.shiftKey) {
        window.open(to, '_blank', 'noopener,noreferrer');
      } else {
        history.push(to, state);
      }
    },
    [history, state, to],
  );
};
